import React, { useState, useEffect } from 'react';
import { X, Download, Upload, MessageCircle, CheckCircle, Clock, XCircle, FileText, MapPin } from 'lucide-react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useTaxListUpload } from '../hooks/useTaxListUpload';

interface OrderDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: {
    orderId: string;
    county: string;
    state: string;
    amount: number;
    status: 'paid' | 'pending' | 'failed';
    fulfilled: boolean;
    createdAt: string;
    downloadUrl?: string;
    supportTickets?: number;
    email?: string;
    stripePaymentId?: string;
  };
  onOpenChat?: () => void;
  onRefresh?: () => void;
  isAdmin?: boolean;
}

export function OrderDetailsModal({ isOpen, onClose, order, onOpenChat, onRefresh, isAdmin = false }: OrderDetailsModalProps) {
  const { uploading: uploadingFile, uploadFile } = useTaxListUpload({
    endpoint: `/admin/taxlatesdata-orders/${order.orderId}/upload-taxlates-list`,
    onSuccess: (result) => {
      onRefresh?.();
      alert(`File uploaded successfully!\n\nFile: ${result.metadata.fileName}\nSize: ${(result.metadata.fileSize / 1024).toFixed(1)} KB\nRecords: ${result.metadata.recordCount}`);
      onClose();
    },
    onError: (error) => {
      alert(`Failed to upload file: ${error.message}`);
    }
  });

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !isAdmin) return;

    try {
      await uploadFile(file);
    } finally {
      // Reset file input
      event.target.value = '';
    }
  };

  const handleDownload = () => {
    if (order.downloadUrl) {
      window.open(order.downloadUrl, '_blank');
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking the backdrop itself, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  if (!isOpen || !order) return null;

  const getStatusIcon = (status: string, fulfilled: boolean) => {
    if (fulfilled) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'paid') return <Clock className="w-5 h-5 text-yellow-600" />;
    if (status === 'failed') return <XCircle className="w-5 h-5 text-red-600" />;
    return <Clock className="w-5 h-5 text-gray-600" />;
  };

  const getStatusText = (status: string, fulfilled: boolean) => {
    if (fulfilled) return 'Delivered';
    if (status === 'paid') return 'Processing (7-10 days)';
    if (status === 'failed') return 'Payment Failed';
    return 'Pending';
  };

  const getStatusColor = (status: string, fulfilled: boolean) => {
    if (fulfilled) return 'text-green-700 bg-green-100';
    if (status === 'paid') return 'text-yellow-700 bg-yellow-100';
    if (status === 'failed') return 'text-red-700 bg-red-100';
    return 'text-gray-700 bg-gray-100';
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-tdb-primary to-tdb-accent p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6 text-white" />
            <div>
              <h2 className="text-2xl font-bold text-white">Order Details</h2>
              <p className="text-sm text-white/80">Order #{order.orderId.slice(0, 12)}...</p>
            </div>
          </div>
          <button
            onClick={handleCloseClick}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            type="button"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
              {/* Order Status */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">Status</h3>
                  <div className={`flex items-center space-x-2 px-4 py-2 rounded-full ${getStatusColor(order.status, order.fulfilled)}`}>
                    {getStatusIcon(order.status, order.fulfilled)}
                    <span className="font-semibold text-sm">{getStatusText(order.status, order.fulfilled)}</span>
                  </div>
                </div>

                {!order.fulfilled && order.status === 'paid' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>⏰ Delivery Timeline:</strong> Your order is being processed. You'll receive an email when your data is ready (typically 7-10 business days).
                    </p>
                  </div>
                )}
              </div>

              {/* Location Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center space-x-2 mb-4">
                  <MapPin className="w-5 h-5 text-tdb-accent" />
                  <h3 className="text-lg font-bold text-gray-900">Location</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">County</div>
                    <div className="text-lg font-semibold text-gray-900">{order.county}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">State</div>
                    <div className="text-lg font-semibold text-gray-900">{order.state}</div>
                  </div>
                </div>
              </div>

              {/* Order Info */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Order Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid</span>
                    <span className="font-semibold text-gray-900">${(order.amount / 100).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Date</span>
                    <span className="font-semibold text-gray-900">
                      {new Date(order.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                  {isAdmin && order.email && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer Email</span>
                      <span className="font-semibold text-gray-900">{order.email}</span>
                    </div>
                  )}
                  {order.stripePaymentId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payment ID</span>
                      <span className="font-mono text-xs text-gray-900">{order.stripePaymentId}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Download Section (if fulfilled) */}
              {order.fulfilled && order.downloadUrl && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-green-900 mb-1">Your Data is Ready!</h3>
                      <p className="text-sm text-green-700">Click below to download your tax delinquent list</p>
                    </div>
                    <button
                      onClick={handleDownload}
                      className="flex items-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-colors font-semibold"
                    >
                      <Download className="w-5 h-5" />
                      <span>Download</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Admin Upload Section */}
              {isAdmin && !order.fulfilled && (
                <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-orange-900 mb-3">Admin: Upload Data File</h3>
                  <div className="relative">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      className="hidden"
                      id="file-upload"
                      accept=".csv,.xlsx,.xls,.zip"
                    />
                    <label
                      htmlFor="file-upload"
                      className={`flex items-center justify-center space-x-2 px-6 py-3 ${
                        uploadingFile
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-orange-600 hover:bg-orange-700 cursor-pointer'
                      } text-white rounded-xl transition-colors font-semibold w-full`}
                    >
                      <Upload className="w-5 h-5" />
                      <span>{uploadingFile ? 'Uploading...' : 'Upload Data File'}</span>
                    </label>
                  </div>
                  <p className="text-xs text-orange-700 mt-2">
                    Accepted formats: CSV, Excel (.xlsx, .xls), ZIP
                  </p>
                </div>
              )}

              {/* Chat/Support Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Need Help?</h3>
                    <p className="text-sm text-gray-600">
                      {order.supportTickets && order.supportTickets > 0
                        ? `You have ${order.supportTickets} message${order.supportTickets > 1 ? 's' : ''}`
                        : 'Chat with our team about this order'}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      onOpenChat?.();
                    }}
                    className="flex items-center space-x-2 px-6 py-3 bg-tdb-accent hover:bg-tdb-accent-dark text-white rounded-xl transition-colors font-semibold"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Open Chat</span>
                  </button>
                </div>
              </div>
            </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
          <button
            onClick={handleCloseClick}
            className="px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl transition-colors font-semibold"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
